// src/components/TokenTicker.js — Light Theme
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { fetchTgenPoolData } from '../utils/tgenPoolApi';

const BUY_URL =
  'https://dex.coinmarketcap.com/token/bsc/0xdd3f2d1b0ec5ea81cdf70e79c713d35ef8d530e1/';

const TickerWrapper = styled.div`
  width: 100%;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  padding: 0.55rem 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
  font-size: 0.85rem;
  color: var(--text-secondary-color);
  gap: 1.4rem;
  flex-wrap: wrap;
`;

const TokenBadge = styled.span`
  padding: 0.22rem 0.65rem;
  border-radius: 999px;
  background: var(--primary-light);
  border: 1px solid rgba(61, 90, 254, 0.2);
  color: var(--primary-color);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 0.72rem;
`;

const Value = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--text-color);
`;

const Label = styled.span`
  color: var(--text-muted);
  margin-right: 0.3rem;
  font-size: 0.82rem;
`;

const blink = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--success-color);
  margin-right: 0.4rem;
  display: inline-block;
  animation: ${blink} 1.4s infinite ease-in-out;
`;

const Change = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: ${({ positive, negative }) =>
    positive ? '#10b981' : negative ? '#ef4444' : 'var(--text-secondary-color)'};
`;

const Divider = styled.span`
  width: 1px;
  height: 18px;
  background: var(--border-color);
  border-radius: 999px;
  display: inline-block;
`;

const BuyButton = styled.a`
  padding: 0.38rem 1rem;
  border-radius: 999px;
  border: 1.5px solid var(--primary-color);
  background: var(--primary-light);
  color: var(--primary-color);
  font-size: 0.8rem;
  font-weight: 700;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
    transform: translateY(-1px);
    box-shadow: var(--shadow-primary);
  }
`;

const TokenTicker = () => {
  const [price, setPrice] = useState(null);
  const [change24h, setChange24h] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let intervalId;

    const fetchData = async () => {
      try {
        const data = await fetchTgenPoolData();
        setPrice(data.priceUsd);
        setChange24h(data.change24h);
        setLoading(false);
      } catch (err) {
        console.error('GeckoTerminal fetch error:', err);
        setLoading(false);
      }
    };

    fetchData();
    intervalId = setInterval(fetchData, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const formattedPrice =
    price != null ? `$${price.toFixed(price < 0.01 ? 6 : 4)}` : '--';

  const formattedChange =
    change24h != null
      ? `${change24h >= 0 ? '+' : ''}${change24h.toFixed(2)}%`
      : '--';

  return (
    <TickerWrapper>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Dot />
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Live on-chain</span>
      </div>

      <Divider />

      <TokenBadge>$TGEN / USDT</TokenBadge>

      <div>
        <Label>Price</Label>
        <Value>{loading ? 'Loading…' : formattedPrice}</Value>
      </div>

      <Divider />

      <div>
        <Label>24h</Label>
        <Change positive={change24h > 0} negative={change24h < 0}>
          {loading ? '—' : formattedChange}
        </Change>
      </div>

      <Divider />

      <BuyButton href={BUY_URL} target="_blank" rel="noopener noreferrer">
        Buy $TGEN ↗
      </BuyButton>
    </TickerWrapper>
  );
};

export default TokenTicker;
